import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { APIKey } from '@feathr/blackbox';
import { Button, EmptyState, Icon, Table, Toolbar } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import apiKeyColumnsV2 from './apiKeyColumnsV2';
import APIKeySecretModal from './APIKeySecretModal';
import { FEATHR_API_DOCS_URL } from './constants';
import CreateAPIKeyModal from './CreateAPIKeyModal';

const PAGE_SIZE = 10;

function APIKeysPage(): JSX.Element {
  const { t } = useTranslation();
  const { APIKeys } = useStore();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // The one-time secret from a successful service-key create. Held only long
  // enough to show it once; null means the secret modal is closed.
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);

  // Mirror the Table's initial request (page 0, PAGE_SIZE) so both resolve to
  // the same apiCache entry — one GET instead of two — and isEmpty derives
  // from the same response the table renders.
  const apiKeysList = APIKeys.list({ pagination: { page: 0, page_size: PAGE_SIZE } });

  function handleOpenCreate(): void {
    setIsCreateOpen(true);
  }

  function handleCloseCreate(): void {
    setIsCreateOpen(false);
  }

  function handleServiceKeyCreated(secret: string): void {
    // Swap the create modal for the one-time secret modal — the secret is on
    // the create response only and can never be shown again.
    setCreatedSecret(secret);
    setIsCreateOpen(false);
  }

  function handleCloseSecret(): void {
    // Discard the secret when the modal closes; it cannot be recovered.
    setCreatedSecret(null);
  }

  const createButton = (
    <Button onClick={handleOpenCreate} prefix={<Icon icon={faPlus} />} type={'primary'}>
      {t('Create key')}
    </Button>
  );

  // Guard on !isErrored so a failed fetch doesn't masquerade as "no keys yet" —
  // an errored list is loaded with zero models, which would otherwise render the
  // empty state and its "Create key" CTA over a failed request.
  const isEmpty = apiKeysList.isLoaded && !apiKeysList.isErrored && apiKeysList.models.length === 0;

  return (
    <Page
      // Per design, the page header always offers "Create key" — the empty
      // state adds a second affordance in the well.
      actions={
        <Toolbar>
          <Button
            href={FEATHR_API_DOCS_URL}
            name={'feathr-api-docs'}
            rel={'noopener noreferrer'}
            suffix={getIconForAction('preview')}
            target={'_blank'}
            type={'secondary'}
          >
            {t('Feathr API docs')}
          </Button>
          {createButton}
        </Toolbar>
      }
      collapseHeader={false}
      description={t('API keys are issued to your account and can be managed by admins.')}
      title={t('API Keys')}
    >
      {isEmpty ? (
        <EmptyState
          description={t('Create a key to get started')}
          height={'full'}
          label={t('No API keys created yet')}
          theme={'white'}
          width={'full'}
        >
          {createButton}
        </EmptyState>
      ) : (
        <Table<APIKey>
          collection={APIKeys}
          columns={apiKeyColumnsV2({ t })}
          initialPagesize={PAGE_SIZE}
          isPaginated={true}
          // Without this the zero-row table falls back to the component's
          // untranslated "No items found for the given filters." — misleading
          // on a filterless page, and the errored path lands here because
          // isEmpty excludes it.
          noDataText={
            apiKeysList.isErrored
              ? t('Unable to load API keys.')
              : t('No API keys have been issued.')
          }
        />
      )}
      <CreateAPIKeyModal
        onClose={handleCloseCreate}
        onServiceKeyCreated={handleServiceKeyCreated}
        opened={isCreateOpen}
      />
      <APIKeySecretModal
        onClose={handleCloseSecret}
        opened={createdSecret !== null}
        secret={createdSecret ?? ''}
      />
    </Page>
  );
}

export default observer(APIKeysPage);
